import { type MutationOptions, useMutation } from '@tanstack/react-query';
import snakecaseKeys from 'snakecase-keys';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

type CompanySettings = {
  addDlnsToInv: boolean;
};

type CompanyArguments = {
  companyDetails: CompanySettings;
  companyId: UUID;
};

/**
 * Adds delivery notes to outgoing invoice for a given company.
 *
 * @param {string} companyId - The ID of the company to add delivery notes to invoices.
 * @param {Object} companyDetails - An object containing the company details.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/add_company_dln_to_invoice_company__id__dln_to_invoice_put
 */
export const addCompanyDlnToInvoice = async (
  companyId: UUID,
  companyDetails: CompanySettings,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.COMPANY.UPDATE_COMPANY_DLN_TO_INVOICE(companyId), {
      searchParams: snakecaseKeys(companyDetails),
    })
    .json();

  return response;
};

/**
 * Custom hook for adding delivery notes to outgoing invoice for a given company.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationAddCompanyDlnToInvoice = (options?: MutationOptions) =>
  useMutation<unknown, Error, CompanyArguments>({
    mutationFn: async ({ companyDetails, companyId }: CompanyArguments) =>
      addCompanyDlnToInvoice(companyId, companyDetails),
    ...options,
  });
